import type { createVuetify } from 'vuetify'
import { setThemeInstance } from '~/cynqueModules/nuxtAccessors'

// vuetify-nuxt-module declares `vuetify:ready` on its own ModuleRuntimeHooks
// interface but does not augment Nuxt's runtime hooks, so `nuxtApp.hook`
// wouldn't type the name. Re-declare it on #app's RuntimeNuxtHooks.
declare module '#app' {
  interface RuntimeNuxtHooks {
    'vuetify:ready': (vuetify: ReturnType<typeof createVuetify>) => void
  }
}

/**
 * Capture the app-wide Vuetify theme instance for cynqueModules/nuxtAccessors.
 *
 * `isDarkTheme()` / `setDarkTheme()` need the `useTheme()` ThemeInstance but
 * run outside component setup(), where the composable can't be called. So we
 * grab the single instance the vuetify-nuxt-module creates and register it as
 * the accessor module's singleton.
 *
 * The module fires `vuetify:ready` right after `app.use(vuetify)`, from a
 * plugin at `order: 25`; this order-0 filesystem plugin runs earlier, so the
 * listener is registered before the hook fires. The fallback covers the
 * reverse ordering (if the Vuetify plugin already ran it will have `provide`d
 * `$vuetify`, so the hook has already fired). The two paths are mutually
 * exclusive — ordering decides which fires — and either instance shape is fine:
 * the accessors read/write it defensively. SPA-only (`ssr: false`) → client.
 */
export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook('vuetify:ready', (vuetify) => {
    setThemeInstance(vuetify.theme)
  })

  const provided = (
    nuxtApp as unknown as { $vuetify?: ReturnType<typeof createVuetify> }
  ).$vuetify
  if (provided?.theme) {
    setThemeInstance(provided.theme)
  }
})
